import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Icon } from "@iconify/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "src/react-query/queryKeys";
import { fetchApi, fetchWithError } from "src/react-query/fetchWithError";
import { mobileCpoChargerInventoryUploadUrl } from "src/react-query/endPoints";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import { debounceFunc } from "src/utils/helper";
import CONNECTOR_TYPES from "src/data/connectorType.json";

const EXCEL_COLUMN_MAP = {
  "CHARGER SERIAL NO.": "serial_no",
  "RATED POWER": "rated_power",
  INSTALLATION: "installation",
  "CHARGE TYPE": "charger_type",
  "CONNECTOR NAME": "connectorName",
  "CONNECTOR TYPE": "connectorType",
  KW: "kw",
  VOLT: "volt",
  DATE: "date",
  "RATING LABEL": "rating_label",
  "RFID CARD": "rfid_card",
  "RFID TAG": "rfid_tag",
};

const REQUIRED_CONNECTOR_FIELDS = ["kw", "volt", "connectorType"];
const CHARGER_TYPES = ["AC", "DC"];
const CONNECTOR_TYPE_VALUE_BY_LABEL = new Map(
  CONNECTOR_TYPES.map((connector) => [connector.type.trim().toLowerCase(), connector.value])
);
const CONNECTOR_TYPE_LABEL_BY_VALUE = new Map(
  CONNECTOR_TYPES.map((connector) => [connector.value, connector.type])
);

export const MOBILE_CPO_CHARGER_INVENTORY = {
  "NOT_CLAIMED": "Not Claimed",
  "CLAIMED": "Claimed",
}

const claimChipColor = (claim) =>
  claim === "CLAIMED" ? "success" : "warning";

const parseChargerExcelFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(new Uint8Array(event.target.result), { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        const errors = [];
        const chargersByChargeBoxId = new Map();

        rows.forEach((row, index) => {
          const parsed = {};
          Object.entries(row).forEach(([header, value]) => {
            const field = EXCEL_COLUMN_MAP[String(header).trim().toUpperCase()];
            if (field) parsed[field] = typeof value === "string" ? value.trim() : value;
          });

          const rowNumber = index + 2; // +1 for header row, +1 for 0-index
          const serialNo = parsed.serial_no;
          if (!serialNo) {
            errors.push({ row: rowNumber, ...parsed, error: '"CHARGER SERIAL NO." is required.' });
            return;
          }

          const [model, chargeBoxId] = String(serialNo).split(":").map((part) => part.trim());
          if (!model || !chargeBoxId) {
            errors.push({
              row: rowNumber,
              ...parsed,
              error: '"CHARGER SERIAL NO." must be in MODEL:CHARGE_BOX_ID:... format.',
            });
            return;
          }

          const connector = {
            kw: parsed.kw,
            volt: parsed.volt,
            connectorType: parsed.connectorType,
            connectorName: parsed.connectorName,
          };
          const hasConnectorData = REQUIRED_CONNECTOR_FIELDS.some(
            (field) => connector[field] !== "" && connector[field] !== undefined
          );
          const missingConnectorFields = REQUIRED_CONNECTOR_FIELDS.filter(
            (field) => connector[field] === "" || connector[field] === undefined
          );
          if (hasConnectorData && missingConnectorFields.length) {
            errors.push({
              row: rowNumber,
              ...parsed,
              error: `${missingConnectorFields.join(", ")} ${
                missingConnectorFields.length > 1 ? "are" : "is"
              } required when connector details are provided.`,
            });
            return;
          }

          const connectorTypeValue = hasConnectorData
            ? CONNECTOR_TYPE_VALUE_BY_LABEL.get(String(connector.connectorType).trim().toLowerCase())
            : undefined;
          if (hasConnectorData && !connectorTypeValue) {
            errors.push({
              row: rowNumber,
              ...parsed,
              error: `"CONNECTOR TYPE" "${connector.connectorType}" is not a recognized type. Must be one of: ${CONNECTOR_TYPES.map((c) => c.type).join(", ")}.`,
            });
            return;
          }

          const chargerType = parsed.charger_type ? String(parsed.charger_type).trim().toUpperCase() : "";
          if (chargerType && !CHARGER_TYPES.includes(chargerType)) {
            errors.push({ row: rowNumber, ...parsed, error: '"CHARGE TYPE" must be AC or DC.' });
            return;
          }

          if (!chargersByChargeBoxId.has(chargeBoxId)) {
            chargersByChargeBoxId.set(chargeBoxId, {
              charge_box_id: chargeBoxId,
              model,
              serial_no: serialNo,
              rated_power: parsed.rated_power,
              installation: parsed.installation,
              charger_type: chargerType || undefined,
              date: parsed.date,
              rating_label: parsed.rating_label,
              rfid_card: parsed.rfid_card,
              rfid_tag: parsed.rfid_tag,
              connectors: [],
            });
          }

          const charger = chargersByChargeBoxId.get(chargeBoxId);
          if (!charger.charger_type && chargerType) charger.charger_type = chargerType;

          if (hasConnectorData) {
            charger.connectors.push({
              kw: connector.kw,
              volt: connector.volt,
              connectorType: connectorTypeValue,
              connectorName: connector.connectorName || undefined,
            });
          }
        });

        if (errors.length) {
          const error = new Error(`${errors.length} row(s) have errors. See details below.`);
          error.failed = errors;
          reject(error);
          return;
        }

        resolve(Array.from(chargersByChargeBoxId.values()));
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });

const ChargerInventoryTab = () => {
  const { teamId: companyId } = useSelectedTeam();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [failedDialogOpen, setFailedDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const debouncedApplySearch = useRef(
    debounceFunc((val) => {
      setPage(0);
      setSearchValue(val);
    }, 600)
  ).current;

  const handleSearchChange = (event) => {
    const val = event.target.value;
    setSearch(val);
    debouncedApplySearch(val);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const { data: response, isFetching } = useQuery(
    queryKeys.mobileCpoChargerInventory(companyId, page, pageSize, searchValue, statusFilter),
    fetchWithError,
    { enabled: !!companyId, keepPreviousData: false }
  );

  const stats = response?.data ?? [];
  const totalCount = response?.total ?? response?.totalCount ?? response?.totalRecord ?? stats.length;

  const uploadMutation = useMutation(fetchApi, {
    onSuccess: (data) => {
      setUploadResult(data?.data ?? null);
      queryClient.removeQueries(["mobileCpoChargerInventory"]);
      setPage(0);
    },
    onError: (error) => {
      const errorData = error?.response?.data;
      if (errorData?.failed?.length) {
        setUploadResult(errorData);
      }
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: errorData?.message ?? error?.message ?? "Upload failed. Please try again.",
          severity: "error",
        })
      );
    },
  });

  const handleDownloadTemplate = () => {
    const headers = Object.keys(EXCEL_COLUMN_MAP);
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Chargers");
    XLSX.writeFile(workbook, "charger_inventory_format.xlsx");
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;

    setUploadResult(null);

    let chargers;
    try {
      chargers = await parseChargerExcelFile(file);
    } catch (error) {
      if (error?.failed?.length) {
        setUploadResult({ added: 0, failed: error.failed });
        setFailedDialogOpen(true);
      }
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: error?.message ?? "Could not parse the uploaded file.",
          severity: "error",
        })
      );
      return;
    }

    if (!chargers.length) {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: 'No rows found. Ensure the file has "CHARGER SERIAL NO.", "RATED POWER", "INSTALLATION" columns.',
          severity: "error",
        })
      );
      return;
    }

    uploadMutation.mutate({
      url: mobileCpoChargerInventoryUploadUrl(companyId),
      method: "POST",
      body: chargers,
    });
  };

  const failedRows = uploadResult?.failed ?? [];
  const failedColumns = Array.from(
    failedRows.reduce((cols, row) => {
      Object.keys(row || {}).forEach((key) => cols.add(key));
      return cols;
    }, new Set())
  );

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Stack direction="row" spacing={1.5}>
          <TextField
            size="small"
            placeholder="Search by charger ID or model"
            value={search}
            onChange={handleSearchChange}
            sx={{ width: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="eva:search-fill" width={18} />
                </InputAdornment>
              ),
            }}
          />
          <Select
            size="small"
            displayEmpty
            value={statusFilter}
            onChange={handleStatusFilterChange}
            sx={{ width: 180 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {Object.entries(MOBILE_CPO_CHARGER_INVENTORY).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </Stack>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        {uploadResult && (
          <Alert
            severity={failedRows.length ? "warning" : "success"}
            action={
              failedRows.length > 0 && (
                <Button color="inherit" size="small" onClick={() => setFailedDialogOpen(true)}>
                  View failed
                </Button>
              )
            }
            sx={{ py: 0 }}
          >
            {uploadResult.added ?? 0} added
            {failedRows.length > 0 ? `, ${failedRows.length} failed` : ""}
          </Alert>
        )}
        <Button
          variant="text"
          startIcon={<Icon icon="mdi:download-outline" width={18} />}
          onClick={handleDownloadTemplate}
        >
          Download Format
        </Button>
        <Button
          variant="outlined"
          component="label"
          startIcon={
            uploadMutation.isLoading ? (
              <CircularProgress size={16} />
            ) : (
              <Icon icon="mdi:file-excel-outline" width={18} />
            )
          }
          disabled={uploadMutation.isLoading || !companyId}
        >
          Upload Excel
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            hidden
            onChange={handleFileChange}
          />
        </Button>
        </Box>
      </Box>

      <TableContainer component={Card} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "background.neutral" }}>
              {[
                "CHARGER ID",
                "MODEL",
                "RATED POWER",
                "INSTALLATION",
                "CHARGE TYPE",
                "CONNECTOR NAME",
                "CONNECTOR TYPE",
                "KW",
                "VOLT",
                "STATUS",
              ].map((col) => (
                <TableCell key={col}>
                  <Typography variant="caption" fontWeight={700} color="text.disabled">
                    {col}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching && stats.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : stats.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.disabled">
                    No chargers found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              stats.map((row) => {
                const connectors = Array.isArray(row.connectors)
                  ? row.connectors
                  : row.connectors
                  ? [row.connectors]
                  : [];
                const joinConnectorField = (field) =>
                  connectors.length === 0
                    ? "—"
                    : connectors.map((connector) => connector[field] ?? "").join(", ");
                const connectorTypeLabels =
                  connectors.length === 0
                    ? "—"
                    : connectors
                        .map((connector) => CONNECTOR_TYPE_LABEL_BY_VALUE.get(connector.connectorType) ?? connector.connectorType ?? "")
                        .join(", ");
                return (
                  <TableRow key={row.id ?? row.charge_box_id} hover>
                    <TableCell><Typography variant="body2">{row.charge_box_id}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{row.model}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{row.rated_power}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{row.installation}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{row.charger_type}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{joinConnectorField("connectorName")}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{connectorTypeLabels}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{joinConnectorField("kw")}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{joinConnectorField("volt")}</Typography></TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={MOBILE_CPO_CHARGER_INVENTORY[row.status]}
                        color={claimChipColor(row.status)}
                        icon={<Icon icon={row.status === "NOT_CLAIMED" ? "mdi:check-circle-outline" : "mdi:clock-outline"} width={14} />}
                        sx={{ borderRadius: 2 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setPageSize(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      <Dialog open={failedDialogOpen} onClose={() => setFailedDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Failed rows ({failedRows.length})</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Fix these rows in your Excel file and re-upload.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {failedColumns.map((col) => (
                    <TableCell key={col}>
                      <Typography variant="caption" fontWeight={700} color="text.disabled">
                        {col}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {failedRows.map((row, index) => (
                  <TableRow key={index} hover>
                    {failedColumns.map((col) => (
                      <TableCell key={col}>
                        <Typography variant="body2">
                          {typeof row[col] === "object" && row[col] !== null
                            ? JSON.stringify(row[col])
                            : String(row[col] ?? "")}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFailedDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ChargerInventoryTab;
